import { useMemo, useState } from "react";
import { gate } from "@/lib/rules";
import { generateInvoicePacking } from "@/lib/merge";
import type { RuleContext } from "@/types";
import { Disclaimer } from "@/components/Disclaimer";
import { FindingsTable, GateSummary } from "@/components/FindingsTable";
import { brokenContext, sampleContext, sampleShipper } from "./sampleDeal";

type Scenario = "ok" | "broken";

export function GateDemoPage(): JSX.Element {
  const [scenario, setScenario] = useState<Scenario>("ok");
  const [merging, setMerging] = useState(false);
  // Acknowledged ERROR rule_ids (BLOCK can never be acknowledged — DP-7).
  const [acked, setAcked] = useState<ReadonlySet<string>>(new Set());

  const ctx: RuleContext = useMemo(
    () => (scenario === "ok" ? sampleContext() : brokenContext()),
    [scenario],
  );
  // Gate re-evaluates with the current acknowledgements (switchScenario clears them).
  const result = useMemo(() => gate(ctx, acked), [ctx, acked]);

  // The set of distinct open-ERROR rule_ids that still require acknowledgement.
  const openErrorRuleIds = useMemo(
    () =>
      [
        ...new Set(
          result.findings
            .filter((f) => f.severity === "ERROR" && f.status === "open")
            .map((f) => f.rule_id),
        ),
      ],
    [result.findings],
  );

  function toggleAck(ruleId: string): void {
    setAcked((prev) => {
      const next = new Set(prev);
      if (next.has(ruleId)) next.delete(ruleId);
      else next.add(ruleId);
      return next;
    });
  }

  function switchScenario(next: Scenario): void {
    setScenario(next);
    setAcked(new Set()); // acknowledgements do not carry across scenarios.
  }

  async function downloadCiPl(): Promise<void> {
    setMerging(true);
    try {
      const buf = await generateInvoicePacking(ctx, { shipper: sampleShipper });
      const blob = new Blob([buf], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `CI-PL-${ctx.deal.deal_no}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setMerging(false);
    }
  }

  return (
    <div>
      <Disclaimer />
      <section className="panel">
        <h2>발행 게이트 데모</h2>
        <p className="sub">
          TORR RF → 미국 1건. 룰엔진/머지 오프라인 동작 (Supabase 불필요).
        </p>

        <div style={{ marginBottom: 16 }}>
          <label style={{ marginRight: 16 }}>
            <input
              type="radio"
              name="scenario"
              checked={scenario === "ok"}
              onChange={() => switchScenario("ok")}
            />{" "}
            정상 케이스 (FCA·IOR/Initial Importer 지정)
          </label>
          <label>
            <input
              type="radio"
              name="scenario"
              checked={scenario === "broken"}
              onChange={() => switchScenario("broken")}
            />{" "}
            의도적 오류 (FOB+항공·수입자 미지정·세율 미확인)
          </label>
        </div>

        <GateSummary
          blocks={result.blocks}
          errors={result.errors}
          warns={result.warns}
          infos={result.infos}
          passed={result.passed}
        />

        <FindingsTable findings={result.findings} />

        {result.blocks > 0 && (
          <p className="gate-block-notice">
            BLOCK {result.blocks}건이 남아 있어 발행할 수 없습니다. BLOCK은
            수출자 통제 내 결정론 룰이므로 확인(ack)으로 해제할 수 없습니다 —
            원인 데이터를 수정하세요.
          </p>
        )}

        {openErrorRuleIds.length > 0 && (
          <div className="ack-list">
            <p className="ack-list-title">
              발행 전 아래 ERROR 항목을 확인(ack)해야 합니다:
            </p>
            {openErrorRuleIds.map((ruleId) => (
              <label key={ruleId} className="ack-item">
                <input
                  type="checkbox"
                  checked={acked.has(ruleId)}
                  onChange={() => toggleAck(ruleId)}
                />{" "}
                <code>{ruleId}</code> 확인함
              </label>
            ))}
          </div>
        )}

        <div style={{ marginTop: 16 }}>
          <button onClick={downloadCiPl} disabled={merging || !result.passed}>
            {merging ? "생성 중…" : "CI + PL xlsx 생성·다운로드"}
          </button>
          {!result.passed && (
            <span className="muted" style={{ marginLeft: 12 }}>
              게이트 미통과 — 발행 차단됨. BLOCK 해소 + ERROR 확인 후 발행
              가능합니다.
            </span>
          )}
        </div>
      </section>
    </div>
  );
}
